import io from "socket.io-client";
import { API_BASE_URL } from "../config/apiConfig";
import { getAuthToken } from "../utils/apiUtils";

// Community namespace (singular) as per backend implementation
const NAMESPACE_PATH = "/community";

class CommunitySocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.currentCommunityId = null;

    // User information for message attribution
    this.userId = null;
    this.userName = null;
    this.userProfilePic = null;

    // Event handlers
    this.callbacks = {
      onNewMessage: null,
      onMessageUpdated: null,
      onMessageDeleted: null,
      onUserJoined: null,
      onUserLeft: null,
      onTypingStart: null,
      onTypingStop: null,
      onPresenceUpdate: null,
    };

    // Notification event listeners (Map of event -> Set of callbacks)
    this.notificationListeners = new Map();

    // Message tracking to prevent duplicates
    this.processedMessages = new Set();
    this.sentMessages = new Map();

    // Clean up tracked messages periodically (every 5 minutes)
    setInterval(() => {
      this.cleanupOldMessages();
    }, 5 * 60 * 1000);
  }

  // ========== NOTIFICATION EVENT SYSTEM ==========
  /**
   * Subscribe to a notification event
   * @param {string} event - Event name
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (!this.notificationListeners.has(event)) {
      this.notificationListeners.set(event, new Set());
    }
    this.notificationListeners.get(event).add(callback);

    // Return unsubscribe function
    return () => {
      const listeners = this.notificationListeners.get(event);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  /**
   * Emit a notification event to all listeners
   * @param {string} event - Event name
   * @param {*} data - Event data
   */
  emit(event, data) {
    const listeners = this.notificationListeners.get(event);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) { /* ignored */ }
      });
    }
  }

  setUserInfo(userId, userName, userProfilePic) {
    this.userId = userId;
    this.userName = userName;
    this.userProfilePic = userProfilePic;
}

  connect() {
    return new Promise((resolve, reject) => {
      // If already connected, return immediately
      if (this.isConnected && this.socket && this.socket.connected) {
resolve(this.socket);
        return;
      }

      // Clean up any existing connection
      if (this.socket) {
this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }

      // Connect to the socket server
      try {
        const token = getAuthToken();

        // Validate API_BASE_URL first
        if (!API_BASE_URL) {
throw new Error("API_BASE_URL not configured");
        }

        // Compute SOCKET_URL fresh at connection time (not module load time)
        // Properly remove /api suffix if present
        let SOCKET_URL = API_BASE_URL.endsWith("/api")
          ? API_BASE_URL.slice(0, -4)
          : API_BASE_URL;

        // Validate URL has proper protocol
        if (
          !SOCKET_URL.startsWith("http://") &&
          !SOCKET_URL.startsWith("https://")
        ) {
throw new Error(
            "Invalid Socket URL configuration - missing protocol"
          );
        }

        // Final validation: make sure URL doesn't have weird patterns
        const fullSocketUrl = SOCKET_URL + NAMESPACE_PATH;
        if (
          fullSocketUrl.includes("https://https") ||
          fullSocketUrl.includes("http://http")
        ) {
throw new Error("Malformed Socket URL - double protocol detected");
        }
if (!token) {
reject(new Error("No auth token available"));
          return;
        }

        // Connect to the community namespace
        this.socket = io(fullSocketUrl, {
          path: "/socket.io/",
          auth: { token: token },
          transports: ["polling", "websocket"],
          withCredentials: true,
          forceNew: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          timeout: 10000,
        });
} catch (error) {
reject(error);
        return;
      }

      // Set up event listeners
      this.socket.on("connect", () => {
this.isConnected = true;
        resolve(this.socket);
      });

      this.socket.on("connect_error", (error) => {
this.isConnected = false;

        // Check if it's an invalid namespace error
        if (error.message && error.message.includes("Invalid namespace")) {
}

        reject(error);
      });

      this.socket.on("disconnect", (reason) => {
this.isConnected = false;
      });

      this.socket.on("reconnect", (attemptNumber) => {
this.isConnected = true;

        // Rejoin community if we were in one
        if (this.currentCommunityId) {
          this.joinCommunity(this.currentCommunityId);
        }
      });

      // Set up community-specific event listeners
      this.setupCommunityEventListeners();
    });
  }

  setupCommunityEventListeners() {
    if (!this.socket) return;

    // Backend now uses proper acknowledgment callbacks (Guide 5 compliant!)
    // Also listen for join_success event as per guide
    this.socket.on("join_success", (data) => {
this.emit("join_success", data);
    });

    // User presence events (Backend uses old names - waiting for Guide 5 update)
    this.socket.on("user_joined", (data) => {
if (this.callbacks.onUserJoined) {
        this.callbacks.onUserJoined(data);
      }
    });

    this.socket.on("user_left", (data) => {
if (this.callbacks.onUserLeft) {
        this.callbacks.onUserLeft(data);
      }
    });

    // Message events
    this.socket.on("new_message", (message) => {
// Prevent duplicate processing
      if (this.processedMessages.has(message._id)) {
return;
      }

      this.processedMessages.add(message._id);

      if (this.callbacks.onNewMessage) {
        this.callbacks.onNewMessage(message);
      }
    });

    this.socket.on("message_updated", (message) => {
if (this.callbacks.onMessageUpdated) {
        this.callbacks.onMessageUpdated(message);
      }
    });

    this.socket.on("message_deleted", (data) => {
if (this.callbacks.onMessageDeleted) {
        this.callbacks.onMessageDeleted(data);
      }
    });

    // Typing indicators - listen for BOTH old and new event names for compatibility
    // Guide says: user_typing, user_stopped_typing
    // Some backends may use: typing_start, typing_stop
    this.socket.on("typing_start", (data) => {
if (this.callbacks.onTypingStart) {
        this.callbacks.onTypingStart(data);
      }
    });

    this.socket.on("typing_stop", (data) => {
if (this.callbacks.onTypingStop) {
        this.callbacks.onTypingStop(data);
      }
    });

    // Guide-compliant typing events
    this.socket.on("user_typing", (data) => {
if (this.callbacks.onTypingStart) {
        this.callbacks.onTypingStart(data);
      }
      this.emit("user_typing", data);
    });

    this.socket.on("user_stopped_typing", (data) => {
if (this.callbacks.onTypingStop) {
        this.callbacks.onTypingStop(data);
      }
      this.emit("user_stopped_typing", data);
    });

    // Presence updates (Guide 5 format)
    this.socket.on("presence_update", (data) => {
if (this.callbacks.onPresenceUpdate) {
        this.callbacks.onPresenceUpdate(data);
      }
    });

    // Socket-specific events
    this.socket.on("error", (error) => {
this.emit("socket_error", error);
    });

    // ========== COMMUNITY NOTIFICATION EVENTS ==========
    // Personal notifications (sent to user's personal channel)
    this.socket.on("notification", (notification) => {
this.emit("notification", notification);
    });

    // Moderator-only notifications
    this.socket.on("moderator_notification", (notification) => {
this.emit("moderator_notification", notification);
    });

    // Thread & Discussion Events
    this.socket.on("new_thread", (thread) => {
this.emit("new_thread", thread);
    });

    this.socket.on("thread_updated", (thread) => {
this.emit("thread_updated", thread);
    });

    this.socket.on("new_reply", (reply) => {
this.emit("new_reply", reply);
    });

    // Membership Events
    this.socket.on("member_joined", (data) => {
this.emit("member_joined", data);
    });

    this.socket.on("member_removed", (data) => {
this.emit("member_removed", data);
    });

    // Join Request Events
    this.socket.on("join_request_received", (data) => {
this.emit("join_request_received", data);
    });

    this.socket.on("join_request_accepted", (data) => {
this.emit("join_request_accepted", data);
    });

    this.socket.on("join_request_rejected", (data) => {
this.emit("join_request_rejected", data);
    });

    // Moderation Events
    this.socket.on("moderation_action", (data) => {
this.emit("moderation_action", data);
    });

    // Community Settings Events
    this.socket.on("community_settings_updated", (data) => {
this.emit("community_settings_updated", data);
    });

    // Presence Events
    this.socket.on("presence_changed", (data) => {
this.emit("presence_changed", data);
    });

    // Reaction Events
    this.socket.on("reaction_added", (data) => {
this.emit("reaction_added", data);
    });

    this.socket.on("reaction_removed", (data) => {
this.emit("reaction_removed", data);
    });

    // Notification count updates (for badge sync)
    this.socket.on("notification_count", (data) => {
this.emit("notification_count", data);
    });

    // Debug: Log all incoming events (can be removed later)
    this.socket.onAny((eventName, ...args) => {
});
  }

  joinCommunity(communityId, onJoinCallback) {
    if (!this.socket || !this.isConnected) {
return;
    }
this.currentCommunityId = communityId;

    // Guide 5: join_community with acknowledgment callback
    this.socket.emit("join_community", { communityId }, (response) => {
      if (response?.success) {
        const onlineUsers = response.data?.onlineUsers || [];
        this.currentCommunityId = communityId;

        // Call the callback with online users data
        if (onJoinCallback && typeof onJoinCallback === "function") {
          onJoinCallback(onlineUsers);
        }
      }
    });
  }

  leaveCommunity(communityId) {
    if (!this.socket || !this.isConnected) {
return;
    }
    this.socket.emit("leave_community", { communityId }, (response) => {
      if (!response?.error) {
        this.currentCommunityId = null;
      }
    });
  }

  sendMessage(communityId, content, type = "text") {
    if (!this.socket || !this.isConnected) {
return Promise.reject(new Error("Socket not connected"));
    }

    return new Promise((resolve, reject) => {
      const messageData = {
        communityId,
        content: content.trim(),
        type,
        tempId: Date.now() + Math.random(), // Temporary ID for tracking
      };
// Track sent message
      this.sentMessages.set(messageData.tempId, Date.now());
// Send message with acknowledgment callback (backend now supports this!)
      this.socket.emit("send_message", messageData, (response) => {
        // Remove from tracking
        this.sentMessages.delete(messageData.tempId);
if (response && response.error) {
reject(new Error(response.error));
        } else {
// Ensure response has proper data structure for optimistic updates
          const normalizedResponse = {
            success: true,
            message: response?.message || "Message sent successfully",
            data: response?.data || {
              _id: response?._id || `temp-${messageData.tempId}`,
              content: content.trim(),
              type,
              sender: {
                _id: this.userId,
                name: this.userName,
                profilePhotoUrl: this.userProfilePic,
              },
              createdAt: new Date().toISOString(),
              community: communityId,
            },
          };

          resolve(normalizedResponse);
        }
      });
    });
  }

  updateMessage(communityId, messageId, content) {
    if (!this.socket || !this.isConnected) {
return Promise.reject(new Error("Socket not connected"));
    }

    return new Promise((resolve, reject) => {
      const updateData = {
        communityId,
        messageId,
        content: content.trim(),
      };
// Send update message with acknowledgment callback
      this.socket.emit("update_message", updateData, (response) => {
if (response && response.error) {
reject(new Error(response.error));
        } else {
resolve({
            success: true,
            message: response?.message || "Message updated successfully",
            data: response?.data || { messageId, content },
          });
        }
      });
    });
  }

  deleteMessage(communityId, messageId) {
    if (!this.socket || !this.isConnected) {
return Promise.reject(new Error("Socket not connected"));
    }

    return new Promise((resolve, reject) => {
      const deleteData = {
        communityId,
        messageId,
      };
// Send delete message with acknowledgment callback
      this.socket.emit("delete_message", deleteData, (response) => {
if (response && response.error) {
reject(new Error(response.error));
        } else {
resolve({
            success: true,
            message: response?.message || "Message deleted successfully",
            data: response?.data || { messageId },
          });
        }
      });
    });
  }

  startTyping(communityId) {
    if (!this.socket || !this.isConnected) return;

    this.socket.emit("typing_start", { communityId });
  }

  stopTyping(communityId) {
    if (!this.socket || !this.isConnected) return;

    this.socket.emit("typing_stop", { communityId });
  }

  updatePresence(presence) {
    if (!this.socket || !this.isConnected) return;

    this.socket.emit("update_presence", { presence });
  }

  // Event handler setters
  onNewMessage(callback) {
    this.callbacks.onNewMessage = callback;
  }

  onMessageUpdated(callback) {
    this.callbacks.onMessageUpdated = callback;
  }

  onMessageDeleted(callback) {
    this.callbacks.onMessageDeleted = callback;
  }

  onUserJoined(callback) {
    this.callbacks.onUserJoined = callback;
  }

  onUserLeft(callback) {
    this.callbacks.onUserLeft = callback;
  }

  onTypingStart(callback) {
    this.callbacks.onTypingStart = callback;
  }

  onTypingStop(callback) {
    this.callbacks.onTypingStop = callback;
  }

  onPresenceUpdate(callback) {
    this.callbacks.onPresenceUpdate = callback;
  }

  cleanupOldMessages() {
    const now = Date.now();
    const fiveMinutesAgo = now - 5 * 60 * 1000;

    // Clean up processed messages (keep only last 1000)
    if (this.processedMessages.size > 1000) {
      const messageIds = Array.from(this.processedMessages);
      const toKeep = messageIds.slice(-500); // Keep last 500
      this.processedMessages = new Set(toKeep);
    }

    // Clean up old sent messages
    for (const [tempId, timestamp] of this.sentMessages.entries()) {
      if (timestamp < fiveMinutesAgo) {
        this.sentMessages.delete(tempId);
      }
    }
  }

  disconnect() {
    if (this.socket) {
this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.currentCommunityId = null;
    }
  }

  debugSocketStatus() {
}

  isSocketConnected() {
    return this.socket && this.socket.connected && this.isConnected;
  }
}

// Export singleton instance
const communitySocketService = new CommunitySocketService();
export default communitySocketService;
